import type { PdfRegion, ShowRegionCommand } from "./types";

export const DEFAULT_DOCUMENT_ID = "active-document";

export type NormalizedShowRegionCommand = {
  requestId: string;
  documentId: string;
  pageIndex: number;
  regions: PdfRegion[];
  replace: boolean;
  behavior: {
    placement: "center" | "nearest";
    scroll: "center" | "nearest" | "none";
    smooth: boolean;
    pulse: boolean;
    durationMs: number;
  };
};

export function normalizeShowRegionCommand(
  command: ShowRegionCommand,
): NormalizedShowRegionCommand {
  if (!command || command.type !== "pdf.show-region") {
    throw new Error("Expected a pdf.show-region command.");
  }

  const regions = command.regions ?? (command.region ? [command.region] : []);
  if (regions.length === 0) {
    throw new Error("Command must include region or regions.");
  }

  if (!Number.isInteger(command.pageIndex) || command.pageIndex < 0) {
    throw new Error("pageIndex must be a zero-based positive integer.");
  }

  return {
    requestId: requireString(command.requestId, "requestId"),
    documentId: command.documentId ?? DEFAULT_DOCUMENT_ID,
    pageIndex: command.pageIndex,
    regions: regions.map(validateRegion),
    replace: command.replace ?? true,
    behavior: {
      placement:
        command.behavior?.placement === "nearest" ? "nearest" : "center",
      scroll: command.behavior?.scroll ?? command.scroll ?? "center",
      smooth: command.behavior?.smooth ?? true,
      pulse: command.behavior?.pulse ?? true,
      durationMs: Number(command.behavior?.durationMs ?? 0),
    },
  };
}

export function requireString(value: unknown, fieldName: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`${fieldName} must be a non-empty string.`);
  }

  return value;
}

function validateRegion(region: PdfRegion): PdfRegion {
  if (region.coordinateSpace === "normalized-page") {
    for (const key of ["x", "y", "width", "height"] as const) {
      requireFiniteNumber(region[key], key);
    }
    return region;
  }

  if (region.coordinateSpace === "pdf-points") {
    for (const key of ["x1", "y1", "x2", "y2"] as const) {
      requireFiniteNumber(region[key], key);
    }
    return region;
  }

  throw new Error("Region coordinateSpace must be normalized-page or pdf-points.");
}

function requireFiniteNumber(value: unknown, fieldName: string) {
  if (!Number.isFinite(value)) {
    throw new Error(`${fieldName} must be a finite number.`);
  }
}
